import { useState, useEffect } from 'react';
import Navbar from '../Navbar';
import { API_BASE } from '../../utils/config';

// Helper API caller
const apiCall = async (url, options = {}) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  const tenant = (parts.length >= 3) ? parts[0] : (parts.length === 2 && parts[1] === 'localhost') ? parts[0] : '';
  const headers = {
    'Content-Type': 'application/json',
    'x-user-id': user.id || '',
    'x-user-role': user.user_type || user.role || 'INTERVIEWER',
    'X-Tenant-ID': tenant,
    ...options.headers
  };
  const baseUrl = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
  const res = await fetch(`${baseUrl}${url}`, { ...options, headers });
  if (!res.ok) throw new Error(`HTTP error ${res.status}`);
  return res.json();
};

export default function InterviewerView() {
  const [activeTab, setActiveTab] = useState('upcoming');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    upcoming: [],
    past: [],
    wallet: { coins: 0, gems: 0 },
    rewardsLog: []
  });

  // Modal grading states
  const [showGradeModal, setShowGradeModal] = useState(false);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [evalForm, setEvalForm] = useState({
    communicationRating: 4,
    technicalRating: 4,
    overallRating: 4,
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const interviewerUser = JSON.parse(localStorage.getItem('user') || '{}');

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const res = await apiCall('/api/interviews/interviewer-dashboard');
      setData(res);
    } catch (err) {
      console.error('Failed to load interviewer dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const handleOpenGradeModal = (interview) => {
    setSelectedInterview(interview);
    setEvalForm({
      communicationRating: 4,
      technicalRating: 4,
      overallRating: 4,
      notes: ''
    });
    setShowGradeModal(true);
  };

  const handleGradeSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      const res = await apiCall('/api/interviews/evaluate', {
        method: 'POST',
        body: JSON.stringify({
          interviewId: selectedInterview.id,
          ...evalForm
        })
      });
      alert(`🎉 Evaluation submitted successfully!\n\n${res.message}`);
      setShowGradeModal(false);
      fetchDashboardData();
    } catch (err) {
      console.error('Failed to submit evaluation:', err);
      alert('Failed to submit evaluation.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const tabs = [
    {
      id: 'upcoming',
      label: 'Upcoming Interviews',
      icon: (
        <svg style={styles.tabIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      )
    },
    {
      id: 'past',
      label: 'Conducted Reviews',
      icon: (
        <svg style={styles.tabIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      id: 'rewards',
      label: 'Coins & Rewards Ledger',
      icon: (
        <svg style={styles.tabIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    }
  ];

  const renderContent = () => {
    if (loading) {
      return <div style={styles.loader}>Loading evaluation workspace...</div>;
    }

    switch (activeTab) {
      case 'upcoming':
        return (
          <div style={styles.tabContainer}>
            <div style={styles.cardHeader}>
              <h2 style={styles.sectionTitle}>Assigned Upcoming Interviews</h2>
              <p style={styles.sectionSubtitle}>Select a candidate below to launch the video lobby or record final evaluation parameters.</p>
            </div>

            <div style={styles.grid}>
              {data.upcoming.length === 0 ? (
                <div style={styles.empty}>No upcoming scheduled interviews assigned to you.</div>
              ) : (
                data.upcoming.map(item => (
                  <div key={item.id} style={styles.interviewCard}>
                    <div style={styles.cardTop}>
                      <span style={styles.roundBadge}>{item.round_type.replace('_', ' ')}</span>
                      <span style={styles.dateLabel}>{new Date(item.scheduled_date).toLocaleString()}</span>
                    </div>
                    <h3 style={styles.candidateName}>{item.candidate_name}</h3>
                    <p style={styles.jobTitle}>Role: {item.job_title}</p>
                    
                    <div style={styles.cardActions}>
                      <button 
                        style={styles.btnLobby}
                        onClick={() => window.open(`/interview/interviewer/${item.room_id}`, '_blank')}
                      >
                        Enter Room
                      </button>
                      <button 
                        style={styles.btnEvaluate}
                        onClick={() => handleOpenGradeModal(item)}
                      >
                        Grade Candidate
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        );
      case 'past':
        return (
          <div style={styles.tabContainer}>
            <div style={styles.cardHeader}>
              <h2 style={styles.sectionTitle}>Conducted Candidate Reviews</h2>
              <p style={styles.sectionSubtitle}>View past grading results and overall scores submitted by you.</p>
            </div>

            <div style={styles.tableCard}>
              {data.past.length === 0 ? (
                <div style={styles.empty}>You have not conducted any evaluations yet.</div>
              ) : (
                <table style={styles.table}>
                  <thead>
                    <tr style={styles.trHeader}>
                      <th style={styles.th}>Candidate</th>
                      <th style={styles.th}>Position</th>
                      <th style={styles.th}>Round Level</th>
                      <th style={styles.th}>Date</th>
                      <th style={styles.th}>Overall Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.past.map(item => (
                      <tr key={item.id} style={styles.tr}>
                        <td style={styles.tdTitle}>{item.candidate_name}</td>
                        <td style={styles.td}>{item.job_title}</td>
                        <td style={styles.td}>{item.round_type.replace('_', ' ')}</td>
                        <td style={styles.td}>{new Date(item.scheduled_date).toLocaleDateString()}</td>
                        <td style={styles.td}>
                          <span style={styles.badgeScore}>★ {item.overall_rating} / 5</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        );
      case 'rewards':
        return (
          <div style={styles.tabContainer}>
            <div style={styles.cardHeader}>
              <h2 style={styles.sectionTitle}>Gamified Rewards Ledger</h2>
              <p style={styles.sectionSubtitle}>Log of points credited to your wallet for conducting evaluations.</p>
            </div>

            <div style={styles.tableCard}>
              <h3 style={styles.tableTitle}>Transaction History</h3>
              {data.rewardsLog.length === 0 ? (
                <div style={styles.empty}>No reward history transactions logged.</div>
              ) : (
                <table style={styles.table}>
                  <thead>
                    <tr style={styles.trHeader}>
                      <th style={styles.th}>Date</th>
                      <th style={styles.th}>Activity Detail</th>
                      <th style={styles.th}>Coins Credited</th>
                      <th style={styles.th}>Gems Credited</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rewardsLog.map(log => (
                      <tr key={log.id} style={styles.tr}>
                        <td style={styles.td}>{new Date(log.created_at).toLocaleDateString()}</td>
                        <td style={styles.tdTitle}>{log.reason}</td>
                        <td style={styles.tdCoins}>+{log.coins_rewarded} Coins</td>
                        <td style={styles.tdGems}>+{log.gems_rewarded} Gems</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div style={styles.wrapper}>
      <Navbar 
        isLoggedIn={true} 
        userName={interviewerUser?.full_name} 
        activeTab="interviewer-dashboard" 
      />
      <div style={styles.dashboardContainer}>
        {/* SIDEBAR */}
        <aside style={styles.sidebar}>
          <div style={styles.userCard}>
            <div style={styles.avatar}>
              {interviewerUser?.full_name ? interviewerUser.full_name.charAt(0).toUpperCase() : 'I'}
            </div>
            <div style={styles.userInfo}>
              <h4 style={styles.userName}>{interviewerUser?.full_name || 'Interviewer'}</h4>
              <p style={styles.userOrg}>{interviewerUser?.org_name || 'Organization'}</p>
              <span style={styles.roleBadge}>CERTIFIED INTERVIEWER</span>
            </div>
          </div>

          <div style={styles.walletBox}>
            <h4 style={styles.walletTitle}>Rewards Balance</h4>
            <div style={styles.walletRow}>
              <div style={styles.walletStat}>
                <span style={styles.walletIconYellow}>🪙</span>
                <div>
                  <div style={styles.walletVal}>{data.wallet.coins}</div>
                  <div style={styles.walletLabel}>Coins</div>
                </div>
              </div>
              <div style={styles.walletStat}>
                <span style={styles.walletIconBlue}>💎</span>
                <div>
                  <div style={styles.walletVal}>{data.wallet.gems}</div>
                  <div style={styles.walletLabel}>Gems</div>
                </div>
              </div>
            </div>
          </div>

          <nav style={styles.navMenu}>
            {tabs.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    ...styles.navItem,
                    ...(isActive ? styles.navItemActive : {})
                  }}
                >
                  {tab.icon}
                  <span style={styles.navLabel}>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* MAIN PANEL */}
        <main style={styles.mainContent}>
          {renderContent()}
        </main>
      </div>

      {/* EVALUATION GRADER MODAL */}
      {showGradeModal && selectedInterview && (
        <div style={styles.modalOverlay} onClick={() => setShowGradeModal(false)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalHeading}>Evaluate Candidate: {selectedInterview.candidate_name}</h3>
            <p style={styles.modalSubheading}>{selectedInterview.round_type.replace('_', ' ')} Grader Rubrics</p>

            <form onSubmit={handleGradeSubmit} style={styles.form}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Communication skills (1-5) : {evalForm.communicationRating}</label>
                <input 
                  type="range" 
                  min="1" 
                  max="5" 
                  value={evalForm.communicationRating}
                  onChange={e => setEvalForm({ ...evalForm, communicationRating: parseInt(e.target.value) })}
                  style={styles.slider}
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Technical Skills (1-5) : {evalForm.technicalRating}</label>
                <input 
                  type="range" 
                  min="1" 
                  max="5" 
                  value={evalForm.technicalRating}
                  onChange={e => setEvalForm({ ...evalForm, technicalRating: parseInt(e.target.value) })}
                  style={styles.slider}
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Overall Recommendation (1-5) : {evalForm.overallRating}</label>
                <input 
                  type="range" 
                  min="1" 
                  max="5" 
                  value={evalForm.overallRating}
                  onChange={e => setEvalForm({ ...evalForm, overallRating: parseInt(e.target.value) })}
                  style={styles.slider}
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Detailed Evaluation Notes</label>
                <textarea 
                  value={evalForm.notes}
                  onChange={e => setEvalForm({ ...evalForm, notes: e.target.value })}
                  placeholder="Summarize candidate's performance, code quality, strengths, and areas of improvement..."
                  rows={4}
                  style={styles.textarea}
                  required
                />
              </div>

              <div style={styles.modalFooter}>
                <button 
                  type="button" 
                  style={styles.btnCancel} 
                  onClick={() => setShowGradeModal(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  style={styles.btnSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Ratings'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  wrapper: {
    minHeight: '100vh',
    backgroundColor: 'var(--bg-page, #f8fafc)',
    color: 'var(--text-main, #0f172a)',
    display: 'flex',
    flexDirection: 'column'
  },
  dashboardContainer: {
    flex: 1,
    display: 'flex',
    maxWidth: '1440px',
    margin: '0 auto',
    width: '100%',
    padding: '24px',
    gap: '24px',
    boxSizing: 'border-box'
  },
  sidebar: {
    width: '280px',
    backgroundColor: 'var(--bg-card-glass, rgba(255, 255, 255, 0.8))',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '16px',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    backdropFilter: 'blur(10px)',
    height: 'fit-content'
  },
  userCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    paddingBottom: '20px',
    borderBottom: '1px solid var(--border-color, #e2e8f0)'
  },
  avatar: {
    width: '52px',
    height: '52px',
    borderRadius: '12px',
    backgroundColor: 'var(--accent-primary, #4f46e5)',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '22px',
    fontWeight: 'bold'
  },
  userInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  userName: {
    margin: 0,
    fontSize: '15px',
    fontWeight: '600'
  },
  userOrg: {
    margin: 0,
    fontSize: '11px',
    color: 'var(--text-muted, #64748b)'
  },
  roleBadge: {
    alignSelf: 'flex-start',
    fontSize: '9px',
    fontWeight: 'bold',
    backgroundColor: 'rgba(13, 148, 136, 0.1)',
    color: '#0d9488',
    padding: '2px 8px',
    borderRadius: '99px'
  },
  walletBox: {
    backgroundColor: 'var(--bg-page, #f8fafc)',
    borderRadius: '12px',
    padding: '16px',
    border: '1px solid var(--border-color, #e2e8f0)'
  },
  walletTitle: {
    margin: '0 0 12px 0',
    fontSize: '12px',
    fontWeight: '700',
    color: 'var(--text-muted, #64748b)',
    textTransform: 'uppercase'
  },
  walletRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px'
  },
  walletStat: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  walletIconYellow: {
    fontSize: '20px'
  },
  walletIconBlue: {
    fontSize: '20px'
  },
  walletVal: {
    fontSize: '18px',
    fontWeight: '700',
    lineHeight: '1.2'
  },
  walletLabel: {
    fontSize: '10px',
    color: 'var(--text-muted, #64748b)'
  },
  navMenu: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    borderRadius: '12px',
    border: 'none',
    backgroundColor: 'transparent',
    color: 'var(--text-muted, #64748b)',
    cursor: 'pointer',
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: '500'
  },
  navItemActive: {
    backgroundColor: 'var(--accent-primary, #4f46e5)',
    color: '#ffffff'
  },
  navLabel: {
    flex: 1
  },
  tabIcon: {
    width: '18px',
    height: '18px'
  },
  mainContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0
  },
  loader: {
    textAlign: 'center',
    padding: '48px',
    color: 'var(--text-muted, #64748b)',
    fontSize: '16px'
  },
  tabContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px'
  },
  cardHeader: {
    backgroundColor: 'var(--bg-card-glass, rgba(255, 255, 255, 0.8))',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '16px',
    padding: '24px',
    backdropFilter: 'blur(10px)'
  },
  sectionTitle: {
    margin: 0,
    fontSize: '24px',
    fontWeight: '700'
  },
  sectionSubtitle: {
    margin: '8px 0 0 0',
    color: 'var(--text-muted, #64748b)',
    fontSize: '14px'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '20px'
  },
  empty: {
    gridColumn: '1 / -1',
    textAlign: 'center',
    padding: '40px',
    color: 'var(--text-muted, #64748b)',
    backgroundColor: 'var(--bg-card-glass, rgba(255, 255, 255, 0.8))',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '16px'
  },
  interviewCard: {
    backgroundColor: 'var(--bg-card-glass, rgba(255, 255, 255, 0.8))',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '16px',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
  },
  cardTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  roundBadge: {
    backgroundColor: 'rgba(79, 70, 229, 0.1)',
    color: 'var(--accent-primary, #4f46e5)',
    padding: '4px 10px',
    borderRadius: '99px',
    fontSize: '10px',
    fontWeight: 'bold',
    textTransform: 'uppercase'
  },
  dateLabel: {
    fontSize: '11px',
    color: 'var(--text-muted, #64748b)',
    fontWeight: '600'
  },
  candidateName: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '700'
  },
  jobTitle: {
    margin: 0,
    fontSize: '13px',
    color: 'var(--text-muted, #64748b)'
  },
  cardActions: {
    display: 'flex',
    gap: '12px',
    marginTop: '8px'
  },
  btnLobby: {
    flex: 1,
    backgroundColor: 'transparent',
    color: '#0f766e',
    border: '1px solid #99f6e4',
    padding: '8px 16px',
    borderRadius: '8px',
    fontWeight: '600',
    cursor: 'pointer',
    fontSize: '13px',
    textAlign: 'center'
  },
  btnEvaluate: {
    flex: 1,
    backgroundColor: 'var(--accent-primary, #4f46e5)',
    color: '#ffffff',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '8px',
    fontWeight: '600',
    cursor: 'pointer',
    fontSize: '13px',
    textAlign: 'center'
  },
  tableCard: {
    backgroundColor: 'var(--bg-card-glass, rgba(255, 255, 255, 0.8))',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '16px',
    padding: '24px',
    backdropFilter: 'blur(10px)'
  },
  tableTitle: {
    margin: '0 0 20px 0',
    fontSize: '18px',
    fontWeight: '600'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    textAlign: 'left'
  },
  trHeader: {
    borderBottom: '2px solid var(--border-color, #e2e8f0)'
  },
  th: {
    padding: '12px 16px',
    color: 'var(--text-muted, #64748b)',
    fontWeight: '600',
    fontSize: '13px'
  },
  tr: {
    borderBottom: '1px solid var(--border-color, #e2e8f0)'
  },
  tdTitle: {
    padding: '16px',
    fontWeight: '600'
  },
  td: {
    padding: '16px',
    fontSize: '14px'
  },
  badgeScore: {
    backgroundColor: '#fef3c7',
    color: '#d97706',
    padding: '4px 10px',
    borderRadius: '99px',
    fontSize: '12px',
    fontWeight: '700'
  },
  tdCoins: {
    padding: '16px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#b45309'
  },
  tdGems: {
    padding: '16px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#1d4ed8'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modalContent: {
    backgroundColor: 'var(--bg-card, #ffffff)',
    border: '1px solid var(--border-color, #e2e8f0)',
    padding: '32px',
    borderRadius: '20px',
    width: '100%',
    maxWidth: '520px',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  modalHeading: {
    margin: 0,
    fontSize: '22px',
    fontWeight: '800'
  },
  modalSubheading: {
    margin: 0,
    fontSize: '13px',
    color: 'var(--text-muted, #64748b)',
    fontWeight: '600',
    textTransform: 'uppercase'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginTop: '12px'
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  slider: {
    width: '100%',
    cursor: 'pointer',
    accentColor: 'var(--accent-primary, #4f46e5)'
  },
  textarea: {
    backgroundColor: 'var(--bg-page, #f8fafc)',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '10px',
    padding: '12px',
    fontSize: '14px',
    fontFamily: 'inherit',
    outline: 'none',
    resize: 'vertical',
    color: 'var(--text-main, #0f172a)'
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '8px'
  },
  btnSubmit: {
    backgroundColor: 'var(--accent-primary, #4f46e5)',
    color: '#ffffff',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '8px',
    fontWeight: '600',
    cursor: 'pointer'
  }
};
